/**
 * HTTP Client for API requests
 * Handles authentication, error handling, and request/response formatting
 */

import type { ApiError, ApiResponse } from '@shared/types'
import { tokenStorage } from '@shared/lib/storage'
import { translateError, HTTP_STATUS_MESSAGES } from './errorTranslations'

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://professionals.vercel.app'

// ============================================================================
// Custom Error Class
// ============================================================================

export class ApiClientError extends Error implements ApiError {
  statusCode?: number
  code?: string
  details?: Record<string, unknown>

  constructor(message: string, statusCode?: number, code?: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiClientError'
    this.statusCode = statusCode
    this.code = code
    this.details = details
  }
}

/**
 * Exact backend rejection message (HTTP 422) returned when a user tries to
 * review their own professional profile. Kept as a constant so the client can
 * distinguish this one expected, user-facing rejection from other legitimate
 * 422 validation errors (e.g. "rating required"), which must still be reported
 * to Sentry. Must stay in sync with the message in professionals-backend
 * `server/controllers/jobReview.controller.js`.
 */
export const SELF_REVIEW_REJECTION_MESSAGE =
  'No podés dejar comentarios a tu propio perfil profesional.'

/**
 * Whether an error is the expected self-review rejection (HTTP 422) — the user
 * tried to review their own professional profile. This is normal user feedback
 * surfaced via `error.message`, NOT a bug, so callers should skip reporting it
 * to Sentry. Deliberately narrow: other 422 validation errors are excluded so
 * they keep being reported as before.
 */
export function isSelfReviewRejectionError(error: unknown): error is ApiClientError {
  return (
    error instanceof ApiClientError &&
    error.statusCode === 422 &&
    typeof error.message === 'string' &&
    error.message.includes(SELF_REVIEW_REJECTION_MESSAGE)
  )
}

// ============================================================================
// Request Configuration
// ============================================================================

interface RequestConfig extends RequestInit {
  requiresAuth?: boolean
  timeout?: number
}

// ============================================================================
// Helper Functions
// ============================================================================

function buildHeaders(config: RequestConfig, body?: unknown): Headers {
  const headers = new Headers(config.headers)

  // Don't set Content-Type for FormData - let the browser set it automatically
  // (it will set multipart/form-data with the boundary)
  const isFormData = body instanceof FormData

  if (!headers.has('Content-Type') && !isFormData) {
    headers.set('Content-Type', 'application/json')
  }

  // Add Authorization header if token exists
  const token = tokenStorage.getToken()
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return headers
}

function buildUrl(endpoint: string): string {
  // Remove leading slash if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint

  return `${API_BASE_URL}/${cleanEndpoint}`
}

interface ErrorBody {
  message?: string | { error?: string; info?: string }
  error?: string
  code?: string
  [key: string]: unknown
}

async function handleResponse<T>(response: Response): Promise<T> {
  // Handle non-JSON responses
  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json')

  if (!response.ok) {
    if (response.status === 401) {
      // Unauthorized - Let components handle the redirect
      // ProtectedRoute component will redirect unauthenticated users
      throw new ApiClientError('No autorizado', 401, 'UNAUTHORIZED')
    }

    // Try to parse error from response
    if (isJson) {
      const errorData = (await response.json()) as ErrorBody
      // Backend sends { message: { error: "..." } } or { message: { info: "..." } }
      const msg = errorData.message
      const rawMessage =
        (typeof msg === 'object' && msg?.error) ||
        (typeof msg === 'object' && msg?.info) ||
        (typeof msg === 'string' ? msg : null) ||
        errorData.error ||
        ''
      throw new ApiClientError(
        translateError(rawMessage, response.status),
        response.status,
        errorData.code,
        errorData as Record<string, unknown>
      )
    }

    // Non-JSON error response - use Spanish HTTP status message
    throw new ApiClientError(
      HTTP_STATUS_MESSAGES[response.status] || 'Error en la solicitud',
      response.status
    )
  }

  // Parse successful response
  if (isJson) {
    const data = (await response.json()) as ApiResponse<T>

    if (!data.success && (data.error || data.message)) {
      // Handle both { error: "string" } and { message: { error: "string" } } formats
      let rawMessage = ''
      if (typeof data.error === 'string') {
        rawMessage = data.error
      } else if (typeof data.message === 'string') {
        rawMessage = data.message
      } else if (data.message && typeof data.message === 'object') {
        rawMessage = data.message.error || data.message.info || ''
      }
      throw new ApiClientError(translateError(rawMessage, response.status), response.status)
    }

    // Handle both wrapped ({ success, data }) and unwrapped responses
    // Some backend endpoints return data directly (e.g., profile endpoint)
    if (data.data !== undefined) {
      return data.data
    }

    // If no 'data' property, assume the response IS the data
    return data as unknown as T
  }

  // Return response as-is for non-JSON responses
  return response as unknown as T
}

// ============================================================================
// Main API Client
// ============================================================================

async function request<T>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<T> {
  const { requiresAuth: _requiresAuth = false, timeout = 30000, ...fetchConfig } = config

  const url = buildUrl(endpoint)
  const headers = buildHeaders(config, fetchConfig.body)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(url, {
      ...fetchConfig,
      headers,
      signal: controller.signal,
      credentials: 'include', // Include httpOnly cookies
    })

    return await handleResponse<T>(response)
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new ApiClientError('La solicitud excedió el tiempo de espera', 408, 'TIMEOUT')
      }

      throw new ApiClientError(error.message, undefined, 'NETWORK_ERROR')
    }

    throw new ApiClientError('Error desconocido', undefined, 'UNKNOWN_ERROR')
  } finally {
    clearTimeout(timeoutId)
  }
}

// ============================================================================
// Convenience Methods
// ============================================================================

export const apiClient = {
  /**
   * GET request
   */
  get: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'GET' }),

  /**
   * POST request
   * Automatically handles FormData and JSON payloads
   */
  post: <T>(endpoint: string, data?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),

  /**
   * PUT request
   * Automatically handles FormData and JSON payloads
   */
  put: <T>(endpoint: string, data?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),

  /**
   * PATCH request
   * Automatically handles FormData and JSON payloads
   */
  patch: <T>(endpoint: string, data?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),

  /**
   * DELETE request
   */
  delete: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'DELETE' }),
}

// ============================================================================
// Exports
// ============================================================================

export { API_BASE_URL }
export type { RequestConfig }

